import { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import ROUTES from '@agency/routes/routes.constant';
import { useConfigAll } from '@core/contexts/ConfigContext';
import { notificationsMutations, notificationsQueries, UserNotificationIndexItem } from '@shared/apis/notifications';
import { adaptNotification, NotificationLinkContext } from '@shared/components/notifications-bell/Notifications.utils';
import { usePusher } from '@shared/contexts/PusherContext';
import { useAxiosMutation, useAxiosQuery } from '@shared/hooks/axios';
import { useEventTracking } from '@shared/hooks/eventTracking.hooks';
import { PusherKeyEnvUser } from '@shared/types/common/events';
import { NotificationsEventPayloads } from '@shared/types/common/eventTracking';
import { ObjectType } from '@shared/types/common/object';
import { NotificationStatus, ResolvedNotification } from '@shared/types/notifications';

// Backstop only: notifications arrive over the user's pusher channel, so this catches
// anything missed while the socket was down.
const POLL_INTERVAL_MS = 60_000;

// Action-type rows (single/double-action, pending/error) are deferred to a
// future notification type (see FE hookup design §7.4). v1 emits link-type rows
// only, so these lifecycle handlers stay inert until the backend drives them.
const noop = () => undefined;

export const useNotifications = () => {
  const history = useHistory();

  const { config, constants } = useConfigAll();
  const tracking = useEventTracking<NotificationsEventPayloads>({ apiSlug: 'notifications' });
  const { channelListen, channelSubscribe } = usePusher();

  const enabled = config.featuresEnabled.webapp.in_app_notifications;

  // Stable reference so the query key — and therefore updateCache — keeps its identity
  // across renders; the pusher subscription effect below depends on updateCache and would
  // otherwise tear down and resubscribe on every render.
  const notificationsPayload = useMemo(() => ({}), []);

  const notificationsQuery = useAxiosQuery(notificationsPayload, notificationsQueries.index, {
    enabled,
    keepPreviousData: true,
    refetchInterval: POLL_INTERVAL_MS,
  });

  const dismissMutation = useAxiosMutation(notificationsMutations.dismiss);
  const readMutation = useAxiosMutation(notificationsMutations.read);

  const notifications: ResolvedNotification[] = useMemo(() => {
    const context: NotificationLinkContext = {
      requirementsExpiringViewId: constants.savedViewIds.req_expiring_society,
      requirementsUnpublishedViewId: constants.savedViewIds.req_unpublished_society,
      target: notificationsQuery.data?.meta?.target ?? null,
      unpublishedTarget: notificationsQuery.data?.meta?.unpublished_target ?? null,
    };

    return (notificationsQuery.data?.data ?? [])
      .map((item) => {
        const adapted = adaptNotification(item, context);

        if (!adapted) {
          return null;
        }

        const resolved: ResolvedNotification = {
          ...adapted,
          status: item.actioned ? NotificationStatus.Actioned : NotificationStatus.Default,
        };

        return resolved;
      })
      .filter((notification): notification is ResolvedNotification => notification !== null);
  }, [
    constants.savedViewIds.req_expiring_society,
    constants.savedViewIds.req_unpublished_society,
    notificationsQuery.data,
  ]);

  // Counts what still wants something from the user. Actioned rows linger in the list only
  // so their success state is visible, so they are not outstanding.
  const outstandingCount = useMemo(
    () => notifications.filter((notification) => notification.status !== NotificationStatus.Actioned).length,
    [notifications]
  );

  // Clicking marks the notification read. It stays in the list showing its actioned state
  // (read does not remove it), so it is optimistically flipped to actioned rather than
  // filtered out.
  const handleItemClick = (item: ResolvedNotification) => {
    tracking.fire('notifications_item_clicked', {
      object_id: parseInt(item.id, 10),
      object_type: ObjectType.Notification,
      notification_type: item.notificationType,
      notification_was_read: item.status === NotificationStatus.Actioned,
    });

    // Only mark read once — an already-actioned row would otherwise re-issue the mutation
    // and refetch on every click. Navigation still happens unconditionally below.
    if (item.status !== NotificationStatus.Actioned) {
      notificationsQuery.updateCache((cache) => ({
        ...cache,
        data: cache.data.map((cacheItem) =>
          cacheItem.id.toString() === item.id ? { ...cacheItem, actioned: true } : cacheItem
        ),
      }));
      readMutation.mutate(
        { id: item.id },
        {
          onSuccess: () => notificationsQuery.refetch(),
        }
      );
    }

    const link = item.link;

    if (link) {
      // Requirement links point at the AngularJS app, so they are absolute urls that
      // the React router cannot handle.
      if (link.startsWith('http')) {
        // Deferred a tick so the tracking and read requests above are dispatched before
        // the full-page navigation unloads the document — an immediate assign() can have
        // the browser cancel them mid-flight.
        setTimeout(() => window.location.assign(link), 0);
      } else {
        history.push(link);
      }
    }
  };

  const handleViewAllInvites = () => {
    history.push(ROUTES.organisation.scheduleInvitees.root);
  };

  const handleDismiss = (item: ResolvedNotification) => {
    tracking.fire('notifications_item_dismissed', {
      object_id: parseInt(item.id, 10),
      object_type: ObjectType.Notification,
      notification_type: item.notificationType,
    });

    notificationsQuery.updateCache((cache) => ({
      ...cache,
      data: cache.data.filter((cacheItem) => cacheItem.id.toString() !== item.id),
    }));
    dismissMutation.mutate(
      { id: item.id },
      {
        onSuccess: () => notificationsQuery.refetch(),
      }
    );
  };

  useEffect(() => {
    if (!enabled || !config.user.id) {
      return undefined;
    }

    const userChannel = channelSubscribe(`${constants.APP_ENV}-App.User.${config.user.id}`);

    const handleNotificationCreated = (notification: UserNotificationIndexItem) => {
      notificationsQuery.updateCache((cache) => ({ ...cache, data: [notification, ...cache.data] }));
    };

    // A grouped row rewritten in place as further match requests arrive moves back to the top.
    const handleNotificationUpdated = (notification: UserNotificationIndexItem) => {
      notificationsQuery.updateCache((cache) => ({
        ...cache,
        data: [notification, ...cache.data.filter((item) => item.id !== notification.id)],
      }));
    };

    const stopListening = [
      channelListen(userChannel, PusherKeyEnvUser.UserNotificationCreated, handleNotificationCreated),
      channelListen(userChannel, PusherKeyEnvUser.UserNotificationUpdated, handleNotificationUpdated),
    ];

    return () => stopListening.forEach((stop) => stop());
  }, [channelListen, channelSubscribe, config.user.id, constants.APP_ENV, enabled, notificationsQuery.updateCache]);

  const result = {
    handleAction: noop,
    handleDismiss,
    handleItemClick,
    handleRetry: noop,
    handleViewAllInvites,
    isLoading: notificationsQuery.isLoading,
    notifications,
    outstandingCount,
  };

  return result;
};
